// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc */
/* eslint-disable ta-code-conventions/has-matching-test */

import React, { type Node, useCallback, useContext, useRef, useState } from 'react';
import { Environment } from '@ta/lithium.context';
import classNames from '@ta/common.classnames';
import styles from './StepsContainer.css';

/**
 * A render prop to render a step
 *
 * @param isCurrentStep - true if the step being rendered is the current step, which should be
 * used to avoid side effects from screens that are displayed during transitions.
 * @param scroll - a callback to set the vertical scroll position of this step
 */
type StepRenderFunction<T> = ({|
  step: T,
  isCurrentStep: boolean,
  scroll: number => mixed,
|}) => Node;

type StyleType = 'hotels';

type Props<T> = {|
  /**  list of steps, including the current one and (possible) steps before and after it. */
  steps: $ReadOnlyArray<T>,

  /** Which step in the supplied list is the current one */
  currentStepIndex: number,

  children: StepRenderFunction<T>,

  /**
   * Callback for when the current step is scrolled. This allows elements outside of the step flow
   * to have customized scrolled styles.
   */
  onScroll?: (scrollPosition: number) => mixed,

  /** Specific styling based on the source it came from */
  type?: StyleType,
|};

/**
 * Wrapper that renders a single step with its own scroll context
 */
const Step = <T>({
  step,
  isVisible,
  isCurrentStep,
  render,
  onScroll,
}: {|
  step: T,
  /**
   * True if this step should be rendered. The current step is always rendered, and during
   * transitions any step that may still be on screen are rendered.
   */
  isVisible: boolean,
  /**
   * True if this is the active step. This is passed to the step contents, and may optionally be
   * used to avoid certain behavior while a step is only being rendered for a transition.
   */
  isCurrentStep: boolean,
  /**
   * The render function supplied to StepContainer
   */
  render: StepRenderFunction<T>,
  onScroll?: (scrollPosition: number) => mixed,
|}) => {
  const wrapperRef = useRef<?HTMLElement>();

  const scroll = useCallback((scrollPosition: number) => {
    if (wrapperRef.current != null) {
      wrapperRef.current.scroll(0, scrollPosition);
    }
  }, []);

  const onScrolled = useCallback(
    (evt: SyntheticUIEvent<HTMLElement>) => {
      if (onScroll != null) {
        onScroll(evt.currentTarget.scrollTop);
      }
    },
    [onScroll],
  );

  return (
    <div
      className={styles.stepWrapper}
      aria-hidden={!isCurrentStep}
      onScroll={onScrolled}
      ref={wrapperRef}
    >
      {isVisible && <div className={styles.step}>{render({ step, isCurrentStep, scroll })}</div>}
    </div>
  );
};

/**
 * Renders a series of dynamic screens, using CSS animations to transition between steps
 */
const StepsContainer = <T: string>({
  steps,
  currentStepIndex,
  children,
  onScroll,
  type,
}: Props<T>) => {
  const { locale } = useContext(Environment);

  // Keep track of the most recently displayed step, so we can render a sliding animation during
  // transitions.
  const [previousStepIndex, setPreviousStepIndex] = useState(currentStepIndex);

  // The range of steps that are currently rendered
  const startIndex = Math.min(previousStepIndex, currentStepIndex);
  const endIndex = Math.max(previousStepIndex, currentStepIndex);

  // Once the sliding transition finishes, only render the current step. This cuts down on the
  // number of DOM nodes we need to display, and prevents future/past steps from getting keyboard
  // focus.
  const transitionEnd = useCallback(
    (evt: SyntheticEvent<*>) => {
      if (evt.target === evt.currentTarget) {
        setPreviousStepIndex(currentStepIndex);
      }
    },
    [currentStepIndex],
  );

  const transform = `translateX(${(locale.isRtl ? 1 : -1) * currentStepIndex * 100}%)`;

  return (
    <div
      className={classNames(styles.steps, {
        [styles.hotels]: type === 'hotels',
      })}
      style={{
        WebkitTransform: transform,
        msTransform: transform,
        transform,
      }}
      onTransitionEnd={transitionEnd}
    >
      {steps.map((step, index) => (
        <Step
          key={step}
          step={step}
          isVisible={index >= startIndex && index <= endIndex}
          isCurrentStep={index === currentStepIndex}
          render={children}
          onScroll={index === currentStepIndex ? onScroll : undefined}
        />
      ))}
    </div>
  );
};

export default StepsContainer;
